import { watch, computed, ref } from 'vue';

export type OrderingType = '-price' | 'price' | 'event_time' | '-event_time' | 'event_name' | 'duration' | '-duration';
export interface OrderingOptions {
  label: string;
  selectedLabel: string;
  value: OrderingType;
}

export function useEventsUrlFiltering() {
  const route = useRoute()
  const router = useRouter();

  const getFilteringFromQuery = (page: number = 1) => {
    const { categories, date, q, ordering, include_past_events } = route.query;
    const filtering = {
      categories: categories ? categories.split(',').filter(Boolean).map(Number) : [],
      date: date ?? null,
      q: q as string,
      order_by: ordering as OrderingType,
      page: page,
      include_past_events: include_past_events ? true : false,
    };

    // remove empty fields
    Object.keys(filtering).forEach(key => {
      if (filtering[key] === null || filtering[key] === undefined || filtering[key] === '') {
        delete filtering[key]
      }
    })

    return filtering
  };

  const clearAll = () => {
    const newQuery = { ...route.query };
    delete newQuery.categories;
    delete newQuery.date;
    delete newQuery.q;
    delete newQuery.order_by;
    delete newQuery.include_past_events;
    router.replace({ query: newQuery });
    search.value = ''
  };

  const clearQueryParam = (param: string) => {
    const newQuery = { ...route.query };
    delete newQuery[param];
    router.replace({ query: newQuery });
  };

  // Sorting
  const ordering = computed(() => {
    return route.query.ordering as OrderingType;
  });
  const orderingOptions = [
    { label: 'Featured First',         selectedLabel: 'Featured First',        value: '-is_featured' },
    { label: 'Price: Low to High',     selectedLabel: 'Price Low to High',     value: 'price' },
    { label: 'Price: High to Low',     selectedLabel: 'Price High to Low',     value: '-price' },
    { label: 'Date: Soonest',          selectedLabel: 'Date Soonest',          value: 'event_time' },
    { label: 'Date: Furthest',         selectedLabel: 'Date Furthest',         value: '-event_time' },
    { label: 'Alphabetically: A to Z', selectedLabel: 'Alphabetically A to Z', value: 'event_name' },
    { label: 'Length: Shortest',       selectedLabel: 'Length Shortest',       value: 'duration' },
    { label: 'Length: Longest',        selectedLabel: 'Length Longest',        value: '-duration' },
  ];
  const updateOrdering = (newOrdering: OrderingType) => {
    if (newOrdering === ordering.value) {
      clearQueryParam('ordering');
    } else if (newOrdering) {
      router.replace({ query: { ...route.query, ordering: newOrdering } });
    } else {
      clearQueryParam('ordering');
    }
  };

  // Date
  const date = computed(() => {
    return route.query.date ? new Date(route.query.date as string) : null;
  });
  const updateDate = (newDate: Date) => {
    if (newDate) {      
      const formattedDate = newDate.toISOString()
      router.replace({ query: { ...route.query, date: formattedDate } });
    } else {
      clearQueryParam('date');
    }
  };

  const clearDate = () => {
    clearQueryParam('date');
  };

  // Search
  const search = ref(route.query.q as string);
  const searchEvents = () => {
    if (search.value) {
      router.replace({ query: { ...route.query, q: search.value } });
    } else {
      clearQueryParam('q');
    }
  };
  watch(() => route.query.q, (newVal) => {
    search.value = newVal as string;
  });

  // Categories
  const selectedCategoriesIds = computed(() => {
    const selectedCategoriesIds = route.query.categories
      ? (route.query.categories as string).split(',').filter(Boolean).map(Number)
      : [];
    return selectedCategoriesIds;
  });

  const toggleCategory = (category: EventCategoriesResponse) => {
    if (selectedCategoriesIds.value.includes(category.id)) {
      const index = selectedCategoriesIds.value.indexOf(category.id);
      selectedCategoriesIds.value.splice(index, 1);
    } else {
      selectedCategoriesIds.value.push(category.id);
    }
    if (selectedCategoriesIds.value.length > 0) {
      router.replace({ query: { ...route.query, categories: selectedCategoriesIds.value.join(',') } });
    } else {
      clearQueryParam('categories');
    }
  };

  // Include past events
  const includePastEvents = computed(() => {
    return route.query.include_past_events ? true : false;
  });

  const toggleIncludePastEvents = () => {
    const newIncludePastEvents = !includePastEvents.value
    if (newIncludePastEvents) {
      router.replace({ query: { ...route.query, include_past_events: 'true' } });
    } else {
      clearQueryParam('include_past_events');
    }
  };

  // All
  const isAnyFilterActive = computed(() => {
    return selectedCategoriesIds.value.length > 0 || 
           !!search.value || 
           !!date.value ||
           includePastEvents.value;
  });

  const numberOfAppliedFilters = computed(() => {
    return selectedCategoriesIds.value.length + (search.value ? 1 : 0) + (date.value ? 1 : 0) + (ordering.value ? 1 : 0) + (includePastEvents.value ? 1 : 0);
  });

  return {
    getFilteringFromQuery,
    clearAll,
    clearQueryParam,
    ordering,
    orderingOptions,
    updateOrdering,
    date,
    updateDate,
    clearDate,
    search,
    searchEvents,
    selectedCategoriesIds,
    toggleCategory,
    isAnyFilterActive,
    numberOfAppliedFilters,
    includePastEvents,
    toggleIncludePastEvents,
  }
}